import React, { createContext, useContext, useState, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { SequenceAvailabilityType, SequenceStatusEnum, SequenceType } from "../../../types/Questionnaire";
import { AnswerValueType } from "../../../types/Answer";
import { QuestionType, QuestionTypeEnum } from "../../../types/Question";
import { useAuth } from "../../../contexts/AuthContext";
import { useConfig } from "../../../contexts/ConfigContext";
import {
  getApiSequenceAvailability,
  getApiSequences,
  getApiFullSequence,
  FullSequenceResponse,
} from "../../../services/questionnaire.service";
import { getUserScores } from "../../../services/sequence.service";

interface Score {
  id: number;
  userSequenceId: number;
  sequenceId: number;
  sequenceComponentId: number;
  questionnaireId: number;
  score: number;
  doneAt: string;
  recurrenceType: "daily" | "weekly" | "fortnightly" | "monthly" | "yearly" | null;
}

interface ResonanceDataContextValue {
  sequences: SequenceType[];
  scores: Score[];
  sequenceAvailabilityBySequenceId: Record<number, SequenceAvailabilityType>;
  hasFetchedSequences: boolean;
  loading: boolean;
  error: Error | null;

  fetchSequences: () => Promise<SequenceType[] | undefined>;
  fetchScores: () => Promise<void>;
  fetchSequenceAvailability: () => Promise<void>;

  fullSequenceByUserSequenceId: Record<number, FullSequenceResponse>;

  // ✅ UPDATED SIGNATURE
  prefetchFullSequencesForStudySequenceIds: (
    studySequenceIds: number[],
    seqs?: SequenceType[]
  ) => Promise<void>;

  updateScore: (
    id: number,
    userSequenceId: number,
    sequenceId: number,
    sequenceComponentId: number,
    questionnaireId: number,
    score: number
  ) => void;

  updateSequenceComponentDone: (userSequenceId: number, sequenceComponentId: number) => void;

  updateSequenceComponentProgress: (
    userSequenceId: number,
    sequenceComponentId: number,
    answeredCount: number
  ) => void;

  updateFullSequenceAnswer: (
    userSequenceId: number,
    questionnaireId: number,
    question: QuestionType,
    value: AnswerValueType
  ) => void;

  updateSequenceDone: (userSequenceId: number) => void;
}

const ResonanceDataContext = createContext<ResonanceDataContextValue>({
  sequences: [],
  scores: [],
  sequenceAvailabilityBySequenceId: {},
  hasFetchedSequences: false,
  loading: false,
  error: null,

  fetchSequences: async () => undefined,
  fetchScores: async () => {},
  fetchSequenceAvailability: async () => {},

  fullSequenceByUserSequenceId: {},
  prefetchFullSequencesForStudySequenceIds: async () => {},

  updateScore: () => {},
  updateSequenceComponentDone: () => {},
  updateSequenceComponentProgress: () => {},
  updateFullSequenceAnswer: () => {},
  updateSequenceDone: () => {},
});

export const ResonanceDataProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const { study } = useConfig();
  const {
    i18n: { language },
  } = useTranslation();

  const [sequences, setSequences] = useState<SequenceType[]>([]);
  const [scores, setScores] = useState<Score[]>([]);
  const [sequenceAvailabilityBySequenceId, setSequenceAvailabilityBySequenceId] =
    useState<Record<number, SequenceAvailabilityType>>({});
  const [hasFetchedSequences, setHasFetchedSequences] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const [fullSequenceByUserSequenceId, setFullSequenceByUserSequenceId] =
    useState<Record<number, FullSequenceResponse>>({});

  const fetchSequences = async (): Promise<SequenceType[] | undefined> => {
    if (!user || !study?.id) return;
    setLoading(true);
    setError(null);

    try {
      const response = await getApiSequences({
        studyId: study.id,
        userId: user.id,
        lang: language,
      });
      setSequences(response.data);
      return response.data;
    } catch (err: any) {
      console.error("Failed to fetch sequences", err);
      setError(err);
      return;
    } finally {
      setHasFetchedSequences(true);
      setLoading(false);
    }
  };

  interface ScoresResponse {
    scores: Score[];
    success?: boolean;
  }

  const fetchScores = async () => {
    if (!user || !study?.id) return;
    try {
      const response = await getUserScores({
        studyId: study.id,
        userId: user.id,
      });

      const raw = response.data;

      let scoresArray: Score[] = [];
      if (Array.isArray(raw)) {
        scoresArray = raw as Score[];
      } else if (raw && Array.isArray((raw as ScoresResponse).scores)) {
        scoresArray = (raw as ScoresResponse).scores;
      }

      setScores(scoresArray);
    } catch (err: any) {
      console.error("Failed to fetch scores", err);
      setError(err);
      setScores([]);
    }
  };

  const fetchSequenceAvailability = async () => {
    if (!user || !study?.id) return;

    try {
      const response = await getApiSequenceAvailability({
        studyId: study.id,
        userId: user.id,
      });

      const availabilityArray = response.data?.availability ?? [];
      setSequenceAvailabilityBySequenceId(
        Object.fromEntries(
          availabilityArray.map((availability) => [availability.sequenceId, availability])
        )
      );
    } catch (err: any) {
      console.error("Failed to fetch sequence availability", err);
      setError(err);
      setSequenceAvailabilityBySequenceId({});
    }
  };

  // ✅ Prefetch FullSequence for inline study sequence ids
  const prefetchFullSequencesForStudySequenceIds = async (
    studySequenceIds: number[],
    seqs?: SequenceType[]
  ) => {
    const source = seqs ?? sequences;
    if (!user || !study?.id) return;
    if (!studySequenceIds?.length) return;

    console.log("Starting prefetch for studySequenceIds:", studySequenceIds);

    const targets = source
      .filter((s) => studySequenceIds.includes(s.sequence_id))
      .map((s) => s.id); // ✅ USER sequence id

    const uniqueTargets = Array.from(new Set(targets)).filter(
      (userSequenceId) => !fullSequenceByUserSequenceId[userSequenceId]
    );

    console.log("Prefetch target userSequenceIds:", uniqueTargets);

    if (!uniqueTargets.length) return;

    const results = await Promise.allSettled(
      uniqueTargets.map((userSequenceId) => {
        console.log("Prefetching FULL sequence for userSequenceId:", userSequenceId);
        return getApiFullSequence({
          studyId: study.id,
          userId: user.id,
          sequenceId: userSequenceId,
          lang: language,
        }).then((r) => ({ userSequenceId, data: r.data }));
      })
    );

    setFullSequenceByUserSequenceId((prev) => {
      const next = { ...prev };
      for (const r of results) {
        if (r.status === "fulfilled") {
          next[r.value.userSequenceId] = r.value.data;
        }
      }
      console.log("Cache updated. Keys now:", Object.keys(next));
      return next;
    });
  };

  // --- existing updateScore / updateSequence* code unchanged ---
  const updateScore = (
    id: number,
    userSequenceId: number,
    sequenceId: number,
    sequenceComponentId: number,
    questionnaireId: number,
    score: number
  ) => {
    setScores((prev) => {
      const existingIndex = prev.findIndex(
        (s) =>
          s.userSequenceId === userSequenceId &&
          s.sequenceComponentId === sequenceComponentId &&
          s.questionnaireId === questionnaireId
      );

      if (existingIndex !== -1) {
        const updated = [...prev];
        updated[existingIndex] = {
          ...updated[existingIndex],
          id,
          score,
          doneAt: new Date().toISOString(),
        };
        return updated;
      }

      return [
        ...prev,
        {
          id,
          userSequenceId,
          sequenceId,
          sequenceComponentId,
          questionnaireId,
          score,
          doneAt: new Date().toISOString(),
          recurrenceType: null,
        },
      ];
    });
  };

  const updateSequenceComponentDone = (userSequenceId: number, sequenceComponentId: number) => {
    setSequences((prev) =>
      prev.map((seq) =>
        seq.id === userSequenceId
          ? {
              ...seq,
              components: seq.components.map((c) =>
                c.id === sequenceComponentId
                  ? { ...c, is_done: true, done_at: new Date().toISOString() }
                  : c
              ),
            }
          : seq
      )
    );
  };

  const updateSequenceComponentProgress = (
    userSequenceId: number,
    sequenceComponentId: number,
    answeredCount: number
  ) => {
    setSequences((prev) =>
      prev.map((seq) =>
        seq.id === userSequenceId
          ? {
              ...seq,
              components: seq.components.map((c) =>
                c.id === sequenceComponentId
                  ? { ...c, answered_questions_count: answeredCount }
                  : c
              ),
            }
          : seq
      )
    );
  };

  const updateFullSequenceAnswer = (
    userSequenceId: number,
    questionnaireId: number,
    question: QuestionType,
    value: AnswerValueType
  ) => {
    setFullSequenceByUserSequenceId((prev) => {
      const current = prev[userSequenceId];

      if (!current) {
        return prev;
      }

      const normalizedAnswerId =
        question.type === QuestionTypeEnum.CHECKBOX
          ? Array.isArray(value)
            ? value.map(Number)
            : []
          : question.type === QuestionTypeEnum.RADIOBUTTON ||
              question.type === QuestionTypeEnum.DROPDOWN
            ? value != null
              ? [Number(value)]
              : []
            : [];

      const normalizedValue = Array.isArray(value) ? value[0] ?? null : value;
      const nextAnswers = (current.answers?.answers ?? []).filter(
        (answer) => answer.question_id !== question.id
      );

      nextAnswers.push({
        questionnaire_id: questionnaireId,
        question_id: question.id,
        question_type_id: Number(question.detail.id ?? 0),
        answer_id: normalizedAnswerId,
        value: normalizedValue,
        is_empty_answer: value == null || (Array.isArray(value) && value.length === 0),
        answer_date: new Date().toISOString(),
      });

      return {
        ...prev,
        [userSequenceId]: {
          ...current,
          answers: {
            ...current.answers,
            answers: nextAnswers,
          },
        },
      };
    });
  };

  const updateSequenceDone = (userSequenceId: number) => {
    setSequences((prev) =>
      prev.map((seq) =>
        seq.id === userSequenceId ? { ...seq, status: SequenceStatusEnum.DONE } : seq
      )
    );
  };

  return (
    <ResonanceDataContext.Provider
      value={{
        sequences,
        scores,
        sequenceAvailabilityBySequenceId,
        hasFetchedSequences,
        loading,
        error,
        fetchSequences,
        fetchScores,
        fetchSequenceAvailability,
        fullSequenceByUserSequenceId,
        prefetchFullSequencesForStudySequenceIds,
        updateScore,
        updateSequenceComponentDone,
        updateSequenceComponentProgress,
        updateFullSequenceAnswer,
        updateSequenceDone,
      }}
    >
      {children}
    </ResonanceDataContext.Provider>
  );
};

export const useResonanceData = () => useContext(ResonanceDataContext);
